import { authFetch } from '../../api/csrf';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  User,
  Edit,
  Building,
  CreditCard,
  Camera,
  X,
  Save,
  Mail,
  Phone,
  Calendar,
  MapPin,
  AlertCircle,
  FileText,
  Download,
  Eye,
  EyeOff,
  IndianRupee,
  Plus,
  Trash2,
  Upload
} from 'lucide-react';
import {
  useProfile,
  useDocuments,
  useComplianceDetails,
  useUpdateProfile,
  useUploadDocument,
  useDeleteTempDocument,
  useUploadProfilePicture,
  useDocumentTypes
} from '../../hooks/useProfileQueries';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import {
  handleTextInputChange,
  handlePhoneInputChange,
  validateText,
  validatePhoneInput,
  getCharacterCount,
  FIELD_LIMITS
} from '../../utils/inputValidation';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../utils/stringUtils';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../components/common/LocationSelect/LocationSelect';

const MyProfile = () => {
  /* React Query Hooks */
  const { data: profileData, isLoading: isProfileLoading, refetch: refetchProfile } = useProfile();
  const employeeData = profileData; // Alias for backward compatibility
  const { data: documentsData, isLoading: isDocumentsLoading } = useDocuments();
  const documents = documentsData || [];
  const { data: documentTypesData, isLoading: isTypesLoading } = useDocumentTypes();
  const documentTypes = documentTypesData || [];
  const { refetch: fetchComplianceDetails, isFetching: isComplianceLoading } = useComplianceDetails();

  const updateProfileMutation = useUpdateProfile();
  const uploadDocumentMutation = useUploadDocument();
  const deleteTempDocumentMutation = useDeleteTempDocument();
  const uploadProfilePictureMutation = useUploadProfilePicture();

  const { user, updateUser } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false); // Kept for general page loading stats if needed, or derived
  const [uploadingPicture, setUploadingPicture] = useState(false);
  // employeeData is now derived from profileData, but we might want a local reference if structure matches
  const [errors, setErrors] = useState({});
  // documents state replaced by documentsData
  const [newDocuments, setNewDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [deletingTempIds, setDeletingTempIds] = useState({});
  // loadingDocuments state replaced by isDocumentsLoading
  const [salaryStructure, setSalaryStructure] = useState(null);
  const profilePictureInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Saved profile data for display
  const [savedProfileData, setSavedProfileData] = useState({
    firstName: '',
    lastName: '',
    position: '',
    department: '',
    employeeId: ''
  });

  // Form state matching AddEmployee structure
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    officialEmail: '',
    mobile: '',
    officialPhone: '',
    dateOfBirth: '',
    gender: '',

    // Employment Information
    employeeId: '',
    department: '',
    position: '',
    location: '',
    dateOfJoining: '',
    employmentType: '',
    manager: '',

    // Compliance/Payroll IDs
    pan: '',
    aadhaar: '',
    uan: '',
    esi: '',
    bankAccount: '',
    ifsc: '',

    // Current Address
    street: '',
    city: null, // Object {name, ...}
    state: null, // Object {name, isoCode, ...}
    country: null, // Object {name, isoCode, ...}
    pincode: '',

    // Permanent Address
    permanentStreet: '',
    permanentCity: null,
    permanentState: null,
    permanentCountry: null,
    permanentPincode: '',
    sameAsCurrentAddress: false
  });

  const [showComplianceDetails, setShowComplianceDetails] = useState(false);
  const [complianceInfo, setComplianceInfo] = useState({
    pan: '',
    aadhaar: '',
    uan: '',
    esi: '',
    bankAccount: '',
    ifsc: ''
  });
  const [loadingComplianceDetails, setLoadingComplianceDetails] = useState(false);
  const [hasLoadedFullCompliance, setHasLoadedFullCompliance] = useState(false);

  // Load employee data on mount, user change, or navigation
  // Sync Profile Data to Form
  useEffect(() => {
    if (profileData) {
      const emp = profileData;

      // Format date helper for input fields
      const formatDateForInput = (date) => {
        if (!date) return '';
        try {
          const d = new Date(date);
          if (isNaN(d.getTime())) return '';
          return d.toISOString().split('T')[0];
        } catch (e) {
          return '';
        }
      };

      // Helper to extract address field (handling object/string and fallback to user address)
      const getAddrField = (empObj, field, isObject = false) => {
        // 1. Try personalInfo.currentAddress (where data seems to be in user response)
        let val = empObj.personalInfo?.currentAddress?.[field];

        // 2. Try contactInfo.currentAddress (schema definition)
        if (!val) {
          val = empObj.contactInfo?.currentAddress?.[field];
        }

        // 3. Fallback to root address (User model structure)
        if (!val) {
          val = empObj.address?.[field === 'pincode' ? 'zipCode' : field];
        }

        if (val) {
          if (isObject) {
            return typeof val === 'object' ? val : { name: val };
          }
          return val;
        }

        return isObject ? null : '';
      };

      // Helper for permanent address
      const getPermAddrField = (empObj, field, isObject = false) => {
        // 1. Try personalInfo.permanentAddress
        let val = empObj.personalInfo?.permanentAddress?.[field];

        // 2. Try contactInfo.permanentAddress
        if (!val) {
          val = empObj.contactInfo?.permanentAddress?.[field];
        }

        if (val) {
          if (isObject) {
            return typeof val === 'object' ? val : { name: val };
          }
          return val;
        }
        return isObject ? null : '';
      };

      // Map to form data structure
      const profileFormData = {
        firstName: emp.personalInfo?.firstName || '',
        lastName: emp.personalInfo?.lastName || '',
        email: emp.personalInfo?.email || '',
        officialEmail: emp.personalInfo?.officialEmail || emp.user?.email || '',
        mobile: emp.personalInfo?.phone || '',
        officialPhone: emp.personalInfo?.officialPhone || emp.user?.phone || '',
        dateOfBirth: formatDateForInput(emp.personalInfo?.dateOfBirth),
        gender: emp.personalInfo?.gender || '',
        employeeId: emp.employeeId || '',
        department: emp.employmentInfo?.department || '',
        position: emp.employmentInfo?.position || '',
        location: (typeof emp.employmentInfo?.workLocation === 'object' ? emp.employmentInfo?.workLocation?.name : emp.employmentInfo?.workLocation) || '',
        dateOfJoining: formatDateForInput(emp.employmentInfo?.joinDate),
        employmentType: emp.employmentInfo?.employmentType || '',
        manager: emp.employmentInfo?.reportingManager || 'Not Assigned',
        pan: emp.complianceInfo?.pan || '',
        aadhaar: emp.complianceInfo?.aadhaar || 'Not provided',
        uan: emp.complianceInfo?.uan || '',
        esi: emp.complianceInfo?.esi || '',
        bankAccount: emp.complianceInfo?.bankAccount || 'Not provided',
        ifsc: emp.complianceInfo?.ifsc || '',

        street: getAddrField(emp, 'street'),
        city: getAddrField(emp, 'city', true),
        state: getAddrField(emp, 'state', true),
        country: getAddrField(emp, 'country', true),
        pincode: getAddrField(emp, 'pincode'),

        permanentStreet: getPermAddrField(emp, 'street'),
        permanentCity: getPermAddrField(emp, 'city', true),
        permanentState: getPermAddrField(emp, 'state', true),
        permanentCountry: getPermAddrField(emp, 'country', true),
        permanentPincode: getPermAddrField(emp, 'pincode'),
        sameAsCurrentAddress: false // Reset initially, user can toggle
      };

      setFormData(profileFormData);

      setComplianceInfo({
        pan: emp.complianceInfo?.pan || 'Not provided',
        aadhaar: emp.complianceInfo?.aadhaar || 'Not provided',
        uan: emp.complianceInfo?.uan || 'Not provided',
        esi: emp.complianceInfo?.esi || 'Not provided',
        bankAccount: emp.complianceInfo?.bankAccount || 'Not provided',
        ifsc: emp.complianceInfo?.ifsc || 'Not provided'
      });

      setShowComplianceDetails(false);
      setHasLoadedFullCompliance(false);

      // Extract and set salary structure data
      const salaryStructureData = emp.salaryStructure || emp.salaryInfo?.salaryStructure || null;
      setSalaryStructure(salaryStructureData);

      // Update saved profile data for header display
      setSavedProfileData({
        firstName: profileFormData.firstName,
        lastName: profileFormData.lastName,
        position: profileFormData.position,
        department: profileFormData.department,
        employeeId: profileFormData.employeeId
      });

      // Sync with global auth context so header refreshes
      // Only if profile picture changed or name changed significantly
      if (user && (
        user.firstName !== profileFormData.firstName ||
        user.lastName !== profileFormData.lastName ||
        (emp.systemInfo?.profilePicture && user.profilePicture !== emp.systemInfo?.profilePicture)
      )) {
        updateUser({
          ...user,
          firstName: profileFormData.firstName,
          lastName: profileFormData.lastName,
          profilePicture: emp.systemInfo?.profilePicture || emp.profileImage || user?.profilePicture
        });
      }
    }
  }, [profileData]); // removed user dependency to avoid loops, profileData change is enough

  // Address Location States
  const [personalCountryOptions, setPersonalCountryOptions] = useState([]);
  const [personalStateOptions, setPersonalStateOptions] = useState([]);
  const [personalCityOptions, setPersonalCityOptions] = useState([]);

  const [permanentStateOptions, setPermanentStateOptions] = useState([]);
  const [permanentCityOptions, setPermanentCityOptions] = useState([]);

  // Load Countries
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map(c => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode,
      ...c
    }));
    setPersonalCountryOptions(formattedCountries);
  }, []);

  // Sync personal state options
  useEffect(() => {
    if (formData.country && typeof formData.country === 'object' && formData.country.isoCode) {
      const states = State.getStatesOfCountry(formData.country.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setPersonalStateOptions(formattedStates);
    } else {
      setPersonalStateOptions([]);
    }
  }, [formData.country]);

  // Sync personal city options
  useEffect(() => {
    if (formData.country && typeof formData.country === 'object' && formData.state && typeof formData.state === 'object') {
      const cities = City.getCitiesOfState(formData.country.isoCode, formData.state.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setPersonalCityOptions(formattedCities);
    } else {
      setPersonalCityOptions([]);
    }
  }, [formData.country, formData.state]);

  // Sync permanent state options
  useEffect(() => {
    if (formData.permanentCountry && typeof formData.permanentCountry === 'object' && formData.permanentCountry.isoCode) {
      const states = State.getStatesOfCountry(formData.permanentCountry.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setPermanentStateOptions(formattedStates);
    } else {
      setPermanentStateOptions([]);
    }
  }, [formData.permanentCountry]);

  // Sync permanent city options
  useEffect(() => {
    if (formData.permanentCountry && typeof formData.permanentCountry === 'object' && formData.permanentState && typeof formData.permanentState === 'object') {
      const cities = City.getCitiesOfState(formData.permanentCountry.isoCode, formData.permanentState.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setPermanentCityOptions(formattedCities);
    } else {
      setPermanentCityOptions([]);
    }
  }, [formData.permanentCountry, formData.permanentState]);

  // Handle Same Address Toggle
  const handleSameAddressToggle = (e) => {
    const isChecked = e.target.checked;
    setFormData(prev => {
      if (isChecked) {
        return {
          ...prev,
          sameAsCurrentAddress: true,
          permanentStreet: prev.street,
          permanentCity: prev.city,
          permanentState: prev.state,
          permanentCountry: prev.country,
          permanentPincode: prev.pincode
        };
      } else {
        return {
          ...prev,
          sameAsCurrentAddress: false
        };
      }
    });
  };

  // Keep addresses synced if toggle is on
  useEffect(() => {
    if (formData.sameAsCurrentAddress) {
      setFormData(prev => ({
        ...prev,
        permanentStreet: prev.street,
        permanentCity: prev.city,
        permanentState: prev.state,
        permanentCountry: prev.country,
        permanentPincode: prev.pincode
      }));
    }
  }, [
    formData.sameAsCurrentAddress,
    formData.street,
    formData.city,
    formData.state,
    formData.country,
    formData.pincode
  ]);

  // Helper for custom select blur
  const handleCustomBlur = (field, value) => {
    // Add any specific blur logic for custom selects if needed
  };



  const maskSensitiveValue = (value) => {
    if (!value || value === 'Not provided') return value || 'Not provided';
    const str = String(value);
    if (str.length <= 4) {
      return '****';
    }
    const start = str.slice(0, 2);
    const end = str.slice(-2);
    return `${start}${'*'.repeat(Math.max(str.length - 4, 4))}${end}`;
  };

  const getComplianceDisplayValue = useCallback((value) => {
    if (!value || value === 'Not provided') return 'Not provided';
    return showComplianceDetails ? value : maskSensitiveValue(value);
  }, [showComplianceDetails]);

  const handleComplianceVisibilityToggle = async () => {
    if (showComplianceDetails) {
      setShowComplianceDetails(false);
      return;
    }

    if (hasLoadedFullCompliance) {
      setShowComplianceDetails(true);
      return;
    }

    try {
      // Use refetch to get data since enabled is false by default
      const { data: result, isError } = await fetchComplianceDetails();

      if (result) {
        setComplianceInfo({
          pan: result.pan || '',
          aadhaar: result.aadhaar || '',
          uan: result.uan || '',
          esi: result.esi || '',
          bankAccount: result.bankAccount || '',
          ifsc: result.ifsc || ''
        });
        setHasLoadedFullCompliance(true);
        setShowComplianceDetails(true);
      } else if (isError) {
        toast.error('Failed to load compliance details');
      }
    } catch (error) {
      toast.error('Failed to load compliance details');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };



  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    // Handle different input types
    if (name === 'mobile' || name === 'phone') {
      const phoneValue = handlePhoneInputChange(e, (newValue) => {
        setFormData(prev => ({ ...prev, [name]: newValue }));
      });
      // Validate phone
      const validation = validatePhoneInput(phoneValue);
      setErrors(prev => ({ ...prev, [name]: validation.error }));
    } else if ((name === 'pincode' || name === 'permanentPincode') && type === 'text') {
      // Postal code specific handling (allow alphanumeric, max length 15)
      const maxLength = 15;
      if (value.length <= maxLength) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    } else if (type === 'text' && name !== 'email') {
      // Text inputs: firstName, lastName
      const maxLength = FIELD_LIMITS[name] || 50;
      const textValue = handleTextInputChange(e, (newValue) => {
        setFormData(prev => ({ ...prev, [name]: newValue }));
      }, maxLength);
      // Validate text
      const validation = validateText(textValue, maxLength);
      setErrors(prev => ({ ...prev, [name]: validation.error }));
    } else {
      // Date, select, email (read-only)
      setFormData(prev => ({ ...prev, [name]: value }));
      // Clear error for this field
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal Information validation with character limits
    if (!formData.firstName || formData.firstName.trim() === '') {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = 'First name cannot exceed 50 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
      newErrors.firstName = 'First name can only contain letters and spaces';
    }

    if (!formData.lastName || formData.lastName.trim() === '') {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = 'Last name cannot exceed 50 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
      newErrors.lastName = 'Last name can only contain letters and spaces';
    }

    if (!formData.mobile || formData.mobile.trim() === '') {
      newErrors.mobile = 'Mobile number is required';
    } else {
      const phoneValidation = validatePhoneInput(formData.mobile);
      if (!phoneValidation.isValid) {
        newErrors.mobile = phoneValidation.error;
      }
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const today = new Date();
      const birthDate = new Date(formData.dateOfBirth);

      if (birthDate > today) {
        newErrors.dateOfBirth = 'Date of birth cannot be in the future';
      }

      // Calculate age
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

      if (actualAge > 100) {
        newErrors.dateOfBirth = 'Date of birth seems too old (more than 100 years)';
      }
      if (actualAge < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // specific validations for fields available in edit mode
    // We only validate these if they are present or required in the strict sense
    // But since this is a profile update, we mainly validate what's provided or visible

    // Compliance/Payroll IDs validation with proper format checking (if editing)
    if (isEditing) {
      // PAN validation - Required
      if (formData.pan && formData.pan !== 'Not provided' && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan.toUpperCase())) {
        newErrors.pan = 'PAN must be in format: ABCDE1234F';
      }

      // Aadhaar validation - Required
      if (formData.aadhaar && formData.aadhaar !== 'Not provided' && !/^\d{12}$/.test(formData.aadhaar)) {
        newErrors.aadhaar = 'Aadhaar must be exactly 12 digits';
      }

      // UAN validation - Optional
      if (formData.uan && formData.uan !== 'Not provided' && !/^\d{12}$/.test(formData.uan)) {
        newErrors.uan = 'UAN must be exactly 12 digits';
      }

      // ESI validation - Optional
      if (formData.esi && formData.esi !== 'Not provided' && !/^\d{10}$/.test(formData.esi)) {
        newErrors.esi = 'ESI must be exactly 10 digits';
      }

      // Bank Account validation - Optional
      if (formData.bankAccount && formData.bankAccount !== 'Not provided' && !/^\d{9,18}$/.test(formData.bankAccount)) {
        newErrors.bankAccount = 'Bank account must be 9-18 digits';
      }

      // IFSC validation - Optional
      if (formData.ifsc && formData.ifsc !== 'Not provided' && formData.ifsc !== 'NOT PROVIDED' && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc.toUpperCase())) {
        newErrors.ifsc = 'IFSC must be in format: ABCD0123456';
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleEditClick = async () => {
    // If we're not yet editing, we might want to load certain things
    if (!isEditing) {
      // Ensure we have full compliance info before editing
      // Always fetch full compliance info when entering edit mode to ensure we have unmasked data
      try {
        const { data: result } = await fetchComplianceDetails();
        if (result) {
          setFormData(prev => ({
            ...prev,
            pan: (result.pan === 'Not provided' || result.pan === 'NOT PROVIDED') ? '' : (result.pan || ''),
            aadhaar: (result.aadhaar === 'Not provided' || result.aadhaar === 'NOT PROVIDED') ? '' : (result.aadhaar || ''),
            uan: (result.uan === 'Not provided' || result.uan === 'NOT PROVIDED') ? '' : (result.uan || ''),
            esi: (result.esi === 'Not provided' || result.esi === 'NOT PROVIDED') ? '' : (result.esi || ''),
            bankAccount: (result.bankAccount === 'Not provided' || result.bankAccount === 'NOT PROVIDED') ? '' : (result.bankAccount || ''),
            bankName: (result.bankName === 'Not provided' || result.bankName === 'NOT PROVIDED') ? '' : (result.bankName || ''),
            bankBranchName: (result.bankBranchName === 'Not provided' || result.bankBranchName === 'NOT PROVIDED') ? '' : (result.bankBranchName || ''),
            ifsc: (result.ifsc === 'Not provided' || result.ifsc === 'NOT PROVIDED') ? '' : (result.ifsc || '')
          }));
          setHasLoadedFullCompliance(true);
        }
      } catch (error) {
        toast.error("Failed to load full compliance details. Some fields may be masked.");
      }
      setIsEditing(true);
      // Filter tab if on a restricted one
      if (['employment', 'salary'].includes(activeTab)) {
        setActiveTab('personal');
      }
    }
  };

  const handleCancel = async () => {
    // Delete newly uploaded documents from Cloudinary
    if (newDocuments.length > 0) {
      const confirmMsg = "You have uploaded documents that are not yet saved. These will be deleted from Cloudinary if you cancel. Continue?";
      if (window.confirm(confirmMsg)) {
        setLoading(true);
        try {
          await Promise.all(newDocuments.map(doc =>
            deleteTempDocumentMutation.mutateAsync({
              publicId: doc.publicId,
              resourceType: doc.resourceType
            })
          ));
          toast.info("Unsaved documents cleaned up.");
        } catch (error) {
        } finally {
          setLoading(false);
        }
      } else {
        return; // User cancelled the cancel action
      }
    }

    setIsEditing(false);
    setNewDocuments([]);
    setErrors({});
    setIsAddingDocument(false);
    setNewDocTitle('');
    refetchProfile(); // Reset form to saved data
  };

  const handleSave = async (e) => {
    // Prevent default form submission if called from form
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!profileData) {
      toast.error('Employee data not loaded. Please refresh the page.');
      return;
    }

    // Validate form before submission
    const validation = validateForm();
    if (!validation.isValid) {
      // Get the first error message to show specific validation error
      const errorFields = Object.keys(validation.errors).filter(key => validation.errors[key]);
      if (errorFields.length > 0) {
        const firstError = validation.errors[errorFields[0]];
        toast.error(firstError || 'Please fix the errors in the form before saving.');
      } else {
        toast.error('Please fill all required fields before saving.');
      }

      // Scroll to first error field for better UX
      setTimeout(() => {
        const firstErrorField = document.querySelector('[aria-invalid="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      // Prepare update data
      const updateData = {
        personalInfo: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          dateOfBirth: formData.dateOfBirth || undefined,
          gender: formData.gender
        },
        contactInfo: {
          personalEmail: formData.email.trim(),
          personalPhone: formData.mobile.trim(),
          currentAddress: {
            street: formData.street || '',
            city: formData.city || { name: '' },
            state: formData.state || { name: '' },
            country: formData.country || { name: '' },
            pincode: formData.pincode || ''
          },
          permanentAddress: {
            street: formData.permanentStreet || '',
            city: formData.permanentCity || { name: '' },
            state: formData.permanentState || { name: '' },
            country: formData.permanentCountry || { name: '' },
            pincode: formData.permanentPincode || ''
          }
        },
        complianceInfo: {
          pan: formData.pan.toUpperCase(),
          aadhaar: formData.aadhaar,
          uan: formData.uan,
          esi: formData.esi,
          bankAccount: formData.bankAccount,
          bankName: formData.bankName,
          bankBranchName: formData.bankBranchName,
          ifsc: formData.ifsc.toUpperCase()
        },
        newDocuments: newDocuments // Array of metadata { name, fileUrl, publicId, etc. }
      };

      // Use mutation
      await updateProfileMutation.mutateAsync(updateData);

      // Success handling
      setIsEditing(false);
      setNewDocuments([]);
      setErrors({});

      // Saved profile data state will be updated by the useEffect watching profileData

      // No need to manually reload - React Query invalidation handles it

    } catch (error) {
      // Error handling is mostly done in mutation onError, but we catch here to stop loading state if needed
      // or specific local error handling
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (max 25MB)
    const MAX_SIZE = 25 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error("File size must be less than 25MB");
      return;
    }

    // Validate file type (only PDF and images)
    const isImage = file.type.startsWith('image/');
    const isPDF = file.type === 'application/pdf';

    if (!isImage && !isPDF) {
      toast.error("Only PDF and image files (JPG, PNG, GIF, WebP) are allowed.");
      return;
    }

    // Check for duplicate names in both current and new docs
    const allDocNames = [
      ...documents.map(d => (d.name || d.originalName || '').toLowerCase()),
      ...newDocuments.map(d => (d.name || d.originalName || '').toLowerCase())
    ];
    if (allDocNames.includes(file.name.toLowerCase())) {
      toast.error("A document with this name already exists.");
      return;
    }

    setIsUploading(true);
    const tempId = `upload-${Date.now()}`;
    setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

    try {
      const result = await uploadDocumentMutation.mutateAsync({
        file,
        onProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [tempId]: percent }));
        }
      });

      if (result) {
        const newDoc = {
          name: newDocTitle.trim() || file.name.split('.')[0],
          originalName: file.name,
          filename: result.public_id,
          fileUrl: result.url,
          fileType: file.type,
          fileSize: file.size,
          publicId: result.public_id,
          resourceType: result.resource_type,
          uploadedAt: new Date(),
          tempId: tempId
        };
        setNewDocuments(prev => [...prev, newDoc]);
        toast.success("Document uploaded successfully.");
        setIsAddingDocument(false);
        setNewDocTitle('');
      }
    } catch (error) {
      // Error handled by mutation logic
    } finally {
      setIsUploading(false);
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[tempId];
        return updated;
      });
      // Clear file input
      if (e.target) e.target.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveNewDocument = async (tempId) => {
    const docToRemove = newDocuments.find(d => d.tempId === tempId);
    if (docToRemove) {
      setDeletingTempIds(prev => ({ ...prev, [tempId]: true }));
      try {
        await deleteTempDocumentMutation.mutateAsync({
          publicId: docToRemove.publicId,
          resourceType: docToRemove.resourceType
        });
        setNewDocuments(prev => prev.filter(d => d.tempId !== tempId));
        toast.info("Document removed.");
      } catch (error) {
        // Error handled by mutation or here
        toast.error("Failed to remove document from server.");
      } finally {
        setDeletingTempIds(prev => ({ ...prev, [tempId]: false }));
      }
    }
  };

  const handleUpdateNewDocMetadata = (tempId, field, value) => {
    setNewDocuments(prev => prev.map(d =>
      d.tempId === tempId ? { ...d, [field]: value } : d
    ));
  };

  // Handle keyboard events (WCAG compliance)
  const handleKeyDown = (e) => {
    // Escape key: Cancel editing
    if (e.key === 'Escape' && isEditing) {
      e.preventDefault();
      setIsEditing(false);
      setErrors({});
      refetchProfile();
    }

    // Enter key: Submit form (only when in edit mode and not in textarea)
    if (e.key === 'Enter' && isEditing && e.target.tagName !== 'TEXTAREA') {
      // Don't submit if focus is on Cancel button
      if (e.target.type === 'button' && e.target.textContent === 'Cancel') {
        return;
      }
      e.preventDefault();
      handleSave(e);
    }
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSave(e);
  };

  // Handle profile picture upload


  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'document').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

    const type = String(mimeType || '').toLowerCase();
    if (type.includes('pdf')) return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type.includes('png')) return `${clean}.png`;
    if (type.includes('jpeg') || type.includes('jpg')) return `${clean}.jpg`;
    if (type.includes('gif')) return `${clean}.gif`;
    if (type.includes('webp')) return `${clean}.webp`;
    return clean;
  };

  const getFilenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const lastPart = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-zA-Z0-9]+$/.test(lastPart) ? lastPart : '';
    } catch (error) {
      return '';
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDocument = (doc) => {
    // Get fileUrl from doc
    let fileUrl = doc.fileUrl || (doc.url && doc.url !== "#" ? doc.url : null);

    // Check if documentData exists and parse it to extract fileUrl
    if (!fileUrl && doc.documentData) {
      try {
        let documentDataParsed;
        if (typeof doc.documentData === 'string') {
          documentDataParsed = JSON.parse(doc.documentData);
        } else {
          documentDataParsed = doc.documentData;
        }

        if (documentDataParsed && documentDataParsed.fileUrl) {
          fileUrl = documentDataParsed.fileUrl;
        }
      } catch (parseError) {
      }
    }

    // Handle base64 data URLs
    if (fileUrl && fileUrl.startsWith('data:')) {
      try {
        const matches = fileUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (matches && matches.length === 3) {
          const mimeType = matches[1];
          const base64Data = matches[2];

          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });

          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          return;
        }
      } catch (base64Error) {
      }
    }

    // Check if document has Cloudinary URL or HTTP URL
    if (fileUrl && (fileUrl.includes('cloudinary.com') || fileUrl.startsWith('http'))) {
      window.open(fileUrl, '_blank');
      return;
    }

    // If no URL available, show error
    if (!fileUrl) {
      toast.error('Document URL not available. Please contact HR.');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Get fileUrl from doc
      let fileUrl = doc.fileUrl || (doc.url && doc.url !== "#" ? doc.url : null);

      // Check if documentData exists and parse it to extract fileUrl
      if (!fileUrl && doc.documentData) {
        try {
          let documentDataParsed;
          if (typeof doc.documentData === 'string') {
            documentDataParsed = JSON.parse(doc.documentData);
          } else {
            documentDataParsed = doc.documentData;
          }

          if (documentDataParsed && documentDataParsed.fileUrl) {
            fileUrl = documentDataParsed.fileUrl;
          }
        } catch (parseError) {
        }
      }

      // Handle base64 data URLs
      if (fileUrl && fileUrl.startsWith('data:')) {
        try {
          const matches = fileUrl.match(/^data:([^;]+);base64,(.+)$/);
          if (matches && matches.length === 3) {
            const mimeType = matches[1];
            const base64Data = matches[2];

            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            const url = window.URL.createObjectURL(blob);
            const preferredName = doc.originalName || doc.filename || doc.name || 'document';
            const filename = ensureFilenameHasExtension(preferredName, mimeType);

            triggerBrowserDownload(url, filename);
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);

            toast.success(`Downloaded ${doc.name || doc.originalName || 'document'} successfully!`);
            return;
          }
        } catch (base64Error) {
        }
      }

      // Check if document has Cloudinary URL or HTTP URL
      if (fileUrl && (fileUrl.includes('cloudinary.com') || fileUrl.startsWith('http'))) {
        const preferredName =
          doc.originalName ||
          doc.filename ||
          doc.name ||
          getFilenameFromUrl(fileUrl) ||
          'document';
        const resolvedFilename = ensureFilenameHasExtension(preferredName, doc.fileType);

        try {
          const response = await authFetch(fileUrl);
          if (!response.ok) throw new Error('Failed to fetch file');
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const finalFilename = ensureFilenameHasExtension(
            resolvedFilename,
            blob.type || doc.fileType
          );
          triggerBrowserDownload(blobUrl, finalFilename);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        } catch (directFetchError) {
          // Fallback for CORS-restricted file URLs.
          triggerBrowserDownload(fileUrl, resolvedFilename);
        }

        toast.success(`Downloading ${doc.name || doc.originalName || resolvedFilename}...`);
        return;
      }

      if (!fileUrl) {
        toast.error('Document URL not available. Please contact HR to access this document.');
      }
    } catch (error) {
      toast.error(`Failed to download ${doc.name || doc.originalName || 'document'}. Please try again.`);
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      // Upload to Cloudinary
      const uploadResult = await uploadProfilePictureMutation.mutateAsync(file);

      if (uploadResult.success && uploadResult.url) {
        // Update profile with new picture URL
        const updateData = {
          systemInfo: {
            profilePicture: uploadResult.url
          }
        };

        await updateProfileMutation.mutateAsync(updateData);
        // Profile caching will handle reload
      }
    } catch (error) {
      // handled by mutation
    } finally {
      setUploadingPicture(false);
      // Reset input
      if (profilePictureInputRef.current) {
        profilePictureInputRef.current.value = '';
      }
    }
  };

  const handleRemoveProfilePicture = async () => {
    // Show a clean confirmation dialog
    const userConfirmed = await window.confirm('Are you sure you want to remove your profile picture? This will reset it to the default icon.');

    if (!userConfirmed) return;

    setUploadingPicture(true);
    try {
      const updateData = {
        systemInfo: {
          profilePicture: ''
        }
      };

      await updateProfileMutation.mutateAsync(updateData);

      // Update local auth user state so header reflects change
      if (user) {
        updateUser({
          ...user,
          profilePicture: ''
        });
      }

      toast.success('Profile picture removed successfully');
    } catch (error) {
      toast.error('Failed to remove profile picture. Please try again.');
    } finally {
      setUploadingPicture(false);
    }
  };

  const renderPersonalDetails = () => (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Profile Header with Photo and Info */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 pb-6 border-b border-gray-200">
          {/* Profile Photo - Larger and better positioned */}
          <div className="relative flex-shrink-0">
            <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-gray-100 shadow-md">
              {employeeData?.systemInfo?.profilePicture ? (
                <img
                  src={employeeData.systemInfo.profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-16 h-16 text-gray-400" />
              )}
            </div>
            {isEditing && (
              <div className="absolute -bottom-2 -right-2 flex flex-col gap-2">
                {employeeData?.systemInfo?.profilePicture && (
                  <button
                    type="button"
                    onClick={handleRemoveProfilePicture}
                    className="bg-red-500 text-white p-2 rounded-full cursor-pointer hover:bg-red-600 shadow-lg transition-all flex items-center justify-center transform hover:scale-105"
                    aria-label="Remove profile picture"
                    title="Remove profile picture"
                    disabled={uploadingPicture}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <label
                  className={`bg-blue-600 text-white p-2.5 rounded-full cursor-pointer hover:bg-blue-700 shadow-lg transition-all flex items-center justify-center transform hover:scale-105 ${uploadingPicture ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label="Upload profile picture"
                  title="Upload profile picture"
                >
                  {uploadingPicture ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                  <input
                    ref={profilePictureInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfilePictureChange}
                    disabled={uploadingPicture}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Name and Position Info */}
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {savedProfileData.firstName} {savedProfileData.lastName}
            </h2>
            <p className="text-lg text-gray-600 mb-1">{savedProfileData.position || 'Position'}</p>
            <p className="text-sm text-gray-500">{savedProfileData.department || 'Department'}</p>
            {savedProfileData.employeeId && (
              <p className="text-xs text-gray-400 mt-2">ID: {savedProfileData.employeeId}</p>
            )}
          </div>
        </div>
      </div>

      {/* Personal Information Form */}
      <form id="profile-form" onSubmit={handleFormSubmit} className="bg-white rounded-lg shadow-sm p-6" noValidate>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2 text-blue-600" />
            Personal Information
          </h3>
          {isEditing && (
            <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded-md uppercase tracking-wider">
              Editing Profile
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={!isEditing}
              maxLength={FIELD_LIMITS.firstName}
              required
              aria-required="true"
              aria-invalid={errors.firstName ? 'true' : 'false'}
              aria-describedby={errors.firstName ? 'firstName-error' : undefined}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 ${errors.firstName ? 'border-red-300' : 'border-blue-300'
                }`}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.firstName && (
                <p id="firstName-error" className="text-sm text-red-600" role="alert">{errors.firstName}</p>
              )}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {getCharacterCount(formData.firstName, FIELD_LIMITS.firstName)} characters
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={!isEditing}
              maxLength={FIELD_LIMITS.lastName}
              required
              aria-required="true"
              aria-invalid={errors.lastName ? 'true' : 'false'}
              aria-describedby={errors.lastName ? 'lastName-error' : undefined}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 ${errors.lastName ? 'border-red-300' : 'border-blue-300'
                }`}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.lastName && (
                <p id="lastName-error" className="text-sm text-red-600" role="alert">{errors.lastName}</p>
              )}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {getCharacterCount(formData.lastName, FIELD_LIMITS.lastName)} characters
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Personal Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={!isEditing}
                className="w-full pl-10 pr-3 py-2 border border-blue-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Official Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="email"
                value={formData.officialEmail || ''}
                readOnly
                className="w-full pl-10 pr-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Personal Mobile <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={!isEditing}
                maxLength={FIELD_LIMITS.mobile}
                required
                aria-required="true"
                aria-invalid={errors.mobile ? 'true' : 'false'}
                aria-describedby={errors.mobile ? 'mobile-error' : undefined}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 ${errors.mobile ? 'border-red-300' : 'border-blue-300'
                  }`}
                placeholder="Enter mobile number (e.g. +91... or 10 digits)"
              />
            </div>
            <div className="flex justify-between items-center mt-1">
              {errors.mobile && (
                <p id="mobile-error" className="text-sm text-red-600" role="alert">{errors.mobile}</p>
              )}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {getCharacterCount(formData.mobile, FIELD_LIMITS.mobile)} chars
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Official Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="tel"
                value={formData.officialPhone || ''}
                readOnly
                className="w-full pl-10 pr-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={!isEditing}
                required
                aria-required="true"
                aria-invalid={errors.dateOfBirth ? 'true' : 'false'}
                aria-describedby={errors.dateOfBirth ? 'dateOfBirth-error' : undefined}
                max={(() => {
                  const today = new Date();
                  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
                  return maxDate.toISOString().split('T')[0];
                })()}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 ${errors.dateOfBirth ? 'border-red-300' : 'border-blue-300'
                  }`}
              />
            </div>
            {errors.dateOfBirth && (
              <p id="dateOfBirth-error" className="mt-1 text-sm text-red-600" role="alert">{errors.dateOfBirth}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={!isEditing}
              required
              aria-required="true"
              aria-invalid={errors.gender ? 'true' : 'false'}
              aria-describedby={errors.gender ? 'gender-error' : undefined}
              className={`w-full px-3 py-2 h-11 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:border-blue-300 ${errors.gender ? 'border-red-300' : 'border-blue-300'
                }`}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {errors.gender && (
              <p id="gender-error" className="mt-1 text-sm text-red-600" role="alert">{errors.gender}</p>
            )}
          </div>
        </div>

        {/* Current Address Section */}
        <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <MapPin className="h-4 w-4 mr-2 text-blue-600" />
            Current Address
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street
              </label>
              <input
                type="text"
                name="street"
                value={formData.street || ''}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="Street, building, area"
                className="w-full px-3 py-2 border border-blue-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:border-blue-300 disabled:text-gray-900"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              {!isEditing ? (
                <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                  {formData.country?.name || 'Not provided'}
                </div>
              ) : (
                <LocationSelect
                  options={personalCountryOptions}
                  value={formData.country}
                  onChange={(country) => {
                    setFormData(prev => ({
                      ...prev,
                      country: country,
                      state: null,
                      city: null
                    }));
                  }}
                  placeholder="Select Country"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              {!isEditing ? (
                <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                  {formData.state?.name || 'Not provided'}
                </div>
              ) : (
                <LocationSelect
                  options={personalStateOptions}
                  value={formData.state}
                  disabled={!formData.country}
                  onChange={(state) => {
                    setFormData(prev => ({
                      ...prev,
                      state: state,
                      city: null
                    }));
                  }}
                  placeholder="Select State"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
              {!isEditing ? (
                <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                  {formData.city?.name || 'Not provided'}
                </div>
              ) : (
                <LocationSelect
                  options={personalCityOptions}
                  value={formData.city}
                  disabled={!formData.state}
                  onChange={(city) => {
                    setFormData(prev => ({
                      ...prev,
                      city: city
                    }));
                  }}
                  placeholder="Select City"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode || ''}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="Postal Code"
                maxLength={15}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:border-blue-300 disabled:text-gray-900 ${errors.pincode ? 'border-red-300' : 'border-blue-300'}`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.pincode && (
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.pincode}
                  </p>
                )}
                {isEditing && (
                  <p className="text-xs text-gray-500 ml-auto">
                    {(formData.pincode || '').length}/15 chars
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Permanent Address Section */}
        <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-blue-600" />
              Permanent Address
            </h3>
            {isEditing && (
              <label className="flex items-center space-x-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formData.sameAsCurrentAddress}
                  onChange={handleSameAddressToggle}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">Same as Current Address</span>
              </label>
            )}
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${formData.sameAsCurrentAddress ? 'opacity-75 pointer-events-none' : ''}`}>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address
              </label>
              <input
                type="text"
                name="permanentStreet"
                value={formData.permanentStreet || ''}
                onChange={handleInputChange}
                readOnly={!isEditing || formData.sameAsCurrentAddress}
                disabled={!isEditing}
                placeholder="Street, building, area"
                className={`w-full px-3 py-2 border border-blue-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:border-blue-300 disabled:text-gray-900 ${formData.sameAsCurrentAddress ? 'bg-gray-100' : ''}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              {!isEditing ? (
                <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                  {formData.permanentCountry?.name || 'Not provided'}
                </div>
              ) : (
                <LocationSelect
                  options={personalCountryOptions}
                  value={formData.permanentCountry}
                  onChange={(country) => {
                    if (formData.sameAsCurrentAddress) return;
                    setFormData(prev => ({
                      ...prev,
                      permanentCountry: country,
                      permanentState: null,
                      permanentCity: null
                    }));
                  }}
                  placeholder="Select Country"
                  disabled={formData.sameAsCurrentAddress}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              {!isEditing ? (
                <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                  {formData.permanentState?.name || 'Not provided'}
                </div>
              ) : (
                <LocationSelect
                  options={permanentStateOptions}
                  value={formData.permanentState}
                  disabled={!formData.permanentCountry || formData.sameAsCurrentAddress}
                  onChange={(state) => {
                    if (formData.sameAsCurrentAddress) return;
                    setFormData(prev => ({
                      ...prev,
                      permanentState: state,
                      permanentCity: null
                    }));
                  }}
                  placeholder="Select State"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
              {!isEditing ? (
                <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                  {formData.permanentCity?.name || 'Not provided'}
                </div>
              ) : (
                <LocationSelect
                  options={permanentCityOptions}
                  value={formData.permanentCity}
                  disabled={!formData.permanentState || formData.sameAsCurrentAddress}
                  onChange={(city) => {
                    if (formData.sameAsCurrentAddress) return;
                    setFormData(prev => ({
                      ...prev,
                      permanentCity: city
                    }));
                  }}
                  placeholder="Select City"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
              <input
                type="text"
                name="permanentPincode"
                value={formData.permanentPincode || ''}
                onChange={handleInputChange}
                readOnly={!isEditing || formData.sameAsCurrentAddress}
                disabled={!isEditing}
                placeholder="Postal Code"
                maxLength={15}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:border-blue-300 disabled:text-gray-900 ${errors.permanentPincode ? 'border-red-300' : 'border-blue-300'} ${formData.sameAsCurrentAddress ? 'bg-gray-100' : 'bg-white'}`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.permanentPincode && (
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.permanentPincode}
                  </p>
                )}
                {isEditing && (
                  <p className="text-xs text-gray-500 ml-auto">
                    {(formData.permanentPincode || '').length}/15 chars
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

      </form>
    </div>
  );

  const renderEmploymentDetails = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building className="h-5 w-5 mr-2 text-blue-600" />
            Employment Information
          </h3>
          <span className="text-sm text-gray-500">Read-only</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employee ID</label>
            <input
              type="text"
              value={formData.employeeId}
              readOnly
              className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <input
              type="text"
              value={formData.department}
              readOnly
              className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
            <input
              type="text"
              value={formData.position}
              readOnly
              className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={formData.location}
                readOnly
                className="w-full pl-10 pr-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Joining</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={formData.dateOfJoining ? (() => {
                  try {
                    const date = new Date(formData.dateOfJoining);
                    if (!isNaN(date.getTime())) {
                      return formatDate(date, { includeTime: false });
                    }
                  } catch (e) { }
                  return formData.dateOfJoining;
                })() : 'Not provided'}
                readOnly
                className="w-full pl-10 pr-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
            <input
              type="text"
              value={formData.employmentType ? formData.employmentType.charAt(0).toUpperCase() + formData.employmentType.slice(1).replace('-', ' ') : ''}
              readOnly
              className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Manager</label>
            <input
              type="text"
              value={formData.manager || 'Not Assigned'}
              readOnly
              className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderComplianceDetails = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <CreditCard className="h-5 w-5 mr-2 text-blue-600" />
            Compliance
          </h3>

        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PAN Number {isEditing && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              name="pan"
              value={isEditing ? (formData.pan === 'Not provided' ? '' : formData.pan) : complianceInfo.pan}
              onChange={handleInputChange}
              disabled={!isEditing}
              maxLength={10}
              placeholder="ABCDE1234F"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : (errors.pan ? 'border-red-300' : 'border-blue-300')}`}
            />
            <div className="flex justify-between items-center mt-1">
              {isEditing && errors.pan && <p className="text-xs text-red-600">{errors.pan}</p>}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.pan.length}/10 characters
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aadhaar Number {isEditing && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              name="aadhaar"
              value={isEditing ? (formData.aadhaar === 'Not provided' ? '' : formData.aadhaar) : complianceInfo.aadhaar}
              onChange={handleInputChange}
              disabled={!isEditing}
              maxLength={12}
              placeholder="12 digit Aadhaar"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : (errors.aadhaar ? 'border-red-300' : 'border-blue-300')}`}
            />
            <div className="flex justify-between items-center mt-1">
              {isEditing && errors.aadhaar && <p className="text-xs text-red-600">{errors.aadhaar}</p>}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.aadhaar.length}/12 digits
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">UAN (Universal Account Number)</label>
            <input
              type="text"
              name="uan"
              value={isEditing ? (formData.uan === 'Not provided' ? '' : formData.uan) : complianceInfo.uan}
              onChange={handleInputChange}
              disabled={!isEditing}
              maxLength={12}
              placeholder="12 digit UAN"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : (errors.uan ? 'border-red-300' : 'border-blue-300')}`}
            />
            <div className="flex justify-between items-center mt-1">
              {isEditing && errors.uan && <p className="text-xs text-red-600">{errors.uan}</p>}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.uan.length}/12 digits
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ESI</label>
            <input
              type="text"
              name="esi"
              value={isEditing ? (formData.esi === 'Not provided' ? '' : formData.esi) : complianceInfo.esi}
              onChange={handleInputChange}
              disabled={!isEditing}
              maxLength={10}
              placeholder="10 digit ESI"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : (errors.esi ? 'border-red-300' : 'border-blue-300')}`}
            />
            <div className="flex justify-between items-center mt-1">
              {isEditing && errors.esi && <p className="text-xs text-red-600">{errors.esi}</p>}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.esi.length}/10 digits
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Account</label>
            <input
              type="text"
              name="bankAccount"
              value={isEditing ? (formData.bankAccount === 'Not provided' ? '' : formData.bankAccount) : complianceInfo.bankAccount}
              onChange={handleInputChange}
              disabled={!isEditing}
              maxLength={18}
              placeholder="Bank Account Number"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : (errors.bankAccount ? 'border-red-300' : 'border-blue-300')}`}
            />
            <div className="flex justify-between items-center mt-1">
              {isEditing && errors.bankAccount && <p className="text-xs text-red-600">{errors.bankAccount}</p>}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.bankAccount.length}/18 digits
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
            <input
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleInputChange}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : 'border-blue-300'}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Branch Name</label>
            <input
              type="text"
              name="bankBranchName"
              value={formData.bankBranchName}
              onChange={handleInputChange}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : 'border-blue-300'}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">IFSC</label>
            <input
              type="text"
              name="ifsc"
              value={isEditing ? (formData.ifsc === 'Not provided' ? '' : formData.ifsc) : complianceInfo.ifsc}
              onChange={handleInputChange}
              disabled={!isEditing}
              maxLength={11}
              placeholder="ABCD0123456"
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${!isEditing ? 'border-blue-300 bg-gray-50 text-gray-900' : (errors.ifsc ? 'border-red-300' : 'border-blue-300')}`}
            />
            <div className="flex justify-between items-center mt-1">
              {isEditing && errors.ifsc && <p className="text-xs text-red-600">{errors.ifsc}</p>}
              {isEditing && (
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.ifsc.length}/11 characters
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const getDocumentDisplayName = (doc) => {
    if (!doc) return 'Untitled Document';
    return doc.name || doc.type || doc.originalName || doc.filename || 'Untitled Document';
  };

  const getDocumentFileName = (doc) => {
    if (!doc) return '';
    return doc.originalName || doc.filename || '';
  };

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            Documents
          </h3>
          {isEditing && !isAddingDocument && (
            <button
              type="button"
              onClick={() => setIsAddingDocument(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold shadow-sm active:scale-95"
            >
              <Plus className="w-4 h-4" />
              <span>Add Document</span>
            </button>
          )}
        </div>

        {isEditing && isAddingDocument && (
          <div className="mb-8 animate-in fade-in slide-in-from-top-2">
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 flex items-center space-x-4">
                  <span className="text-sm font-bold text-gray-700 uppercase whitespace-nowrap">Document Title</span>
                  <select
                    value={newDocTitle}
                    onChange={(e) => setNewDocTitle(e.target.value)}
                    className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer max-w-md"
                    disabled={isUploading || isTypesLoading}
                    autoFocus
                  >
                    <option value="">Select Document Type</option>
                    {isTypesLoading ? (
                      <option disabled>Loading types...</option>
                    ) : documentTypes.length === 0 ? (
                      <option disabled>No types available</option>
                    ) : (
                      documentTypes.map((type, index) => {
                        const value = typeof type === 'string' ? type : (type.name || type.label || '');
                        const label = value.charAt(0).toUpperCase() + value.slice(1);
                        return (
                          <option key={type._id || index} value={value}>
                            {label}
                          </option>
                        );
                      })
                    )}
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="application/pdf,image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading || !newDocTitle.trim()}
                    className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-all font-semibold"
                  >
                    {isUploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        <span>Upload Document</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingDocument(false);
                      setNewDocTitle('');
                    }}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Cancel"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {(isDocumentsLoading && documents.length === 0) ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading documents...</p>
            </div>
          </div>
        ) : (documents.length === 0 && newDocuments.length === 0 && !isAddingDocument) ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No documents found</p>
            <p className="text-sm text-gray-500 mt-2">
              {isEditing ? "Add a new document using the button above" : "Documents uploaded by HR will appear here"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* New Documents (Unsaved) */}
            {newDocuments.map((doc) => (
              <div
                key={doc.tempId}
                className="bg-white border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 overflow-hidden">
                    <FileText className="h-5 w-5 text-blue-500 shrink-0" />
                    <span className="text-sm font-semibold text-gray-900 truncate">
                      {doc.name}
                    </span>
                    <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded uppercase font-bold shrink-0">New</span>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      onClick={() => handleRemoveNewDocument(doc.tempId)}
                      disabled={deletingTempIds[doc.tempId]}
                      className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                      title="Remove"
                    >
                      {deletingTempIds[doc.tempId] ? (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Existing Documents */}
            {documents.map((doc, index) => (
              <div
                key={doc.id || doc._id || index}
                className="bg-white border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-3 overflow-hidden">
                    <FileText className="h-5 w-5 text-gray-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {getDocumentDisplayName(doc)}
                      </p>
                      {getDocumentFileName(doc) && getDocumentFileName(doc) !== getDocumentDisplayName(doc) && (
                        <p className="text-xs text-gray-500 truncate">
                          {getDocumentFileName(doc)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                   
                    <button
                      onClick={() => handleDownloadDocument(doc)}
                      className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderSalaryStructure = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <IndianRupee className="h-5 w-5 mr-2 text-blue-600" />
            Salary Structure
          </h3>
          <span className="text-sm text-gray-500">Read-only</span>
        </div>

        {salaryStructure ? (
          <>
            {/* Salary Components */}
            {salaryStructure.components && salaryStructure.components.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  Salary Components
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Component Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        // Calculate totals by type once
                        const earnings = salaryStructure.components
                          .filter(c => c.type === 'earning')
                          .reduce((sum, c) => sum + (c.value || 0), 0);
                        const deductions = salaryStructure.components
                          .filter(c => c.type === 'deduction')
                          .reduce((sum, c) => sum + (c.value || 0), 0);
                        const reimbursements = salaryStructure.components
                          .filter(c => c.type === 'reimbursement')
                          .reduce((sum, c) => sum + (c.value || 0), 0);

                        return (
                          <>
                            {salaryStructure.components.map((component, index) => (
                              <tr key={component._id || component.id || index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 capitalize">
                                  {component.name || 'N/A'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${component.type === 'earning'
                                    ? 'bg-green-100 text-green-800'
                                    : component.type === 'deduction'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-blue-100 text-blue-800'
                                    }`}>
                                    {component.type || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                  ₹{(component.value || 0).toLocaleString('en-IN')}
                                </td>
                              </tr>
                            ))}
                            {/* Show totals after all components */}
                            <tr className="bg-gray-50">
                              <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-gray-900">
                                Total Earnings
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-green-700 text-right">
                                ₹{earnings.toLocaleString('en-IN')}
                              </td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-gray-900">
                                Total Deductions
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-red-700 text-right">
                                ₹{deductions.toLocaleString('en-IN')}
                              </td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-gray-900">
                                Total Reimbursements
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-blue-700 text-right">
                                ₹{reimbursements.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Net Pay Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">
                  Net Pay
                </span>
                <span className="text-2xl font-bold text-blue-600">
                  ₹{(salaryStructure.netPay || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Net Pay = Earnings + Reimbursements
              </p>
            </div>
          </>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              No salary structure assigned to this employee.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const tabs = [
    { id: 'personal', label: 'Personal Information', icon: User },
    { id: 'employment', label: 'Employment Information', icon: Building, hideInEdit: true },
    { id: 'compliance', label: 'Compliance', icon: CreditCard },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'salary', label: 'Salary Structure', icon: IndianRupee, hideInEdit: true },
  ];

  const visibleTabs = isEditing ? tabs.filter(t => !t.hideInEdit) : tabs;

  if (isProfileLoading && !profileData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData && !isProfileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600 mb-4">
            Unable to load your profile data. Please contact HR if this issue persists.
          </p>
          <button
            onClick={() => refetchProfile()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 relative" onKeyDown={handleKeyDown}>
      <div className="mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 border-none">My Profile</h1>
            <p className="text-gray-500 mt-1">View and manage your professional profile</p>
          </div>
          {!isEditing && (
            <button
              onClick={handleEditClick}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md transition-all active:scale-95"
            >
              <Edit className="w-5 h-5" />
              <span className="font-semibold">Edit Profile</span>
            </button>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="border-b border-gray-200 px-2 overflow-x-auto no-scrollbar">
            <nav className="flex space-x-1 md:space-x-4">
              {visibleTabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      if (!isEditing) setIsEditing(false); // Already false, but just in case
                    }}
                    className={`py-4 px-4 font-medium text-sm flex items-center space-x-2 whitespace-nowrap border-b-2 transition-all duration-200 ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50/30'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <IconComponent className={`w-4 h-4 ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          {activeTab === 'personal' && renderPersonalDetails()}
          {activeTab === 'employment' && !isEditing && renderEmploymentDetails()}
          {activeTab === 'compliance' && renderComplianceDetails()}
          {activeTab === 'documents' && renderDocuments()}
          {activeTab === 'salary' && !isEditing && renderSalaryStructure()}
        </div>

        {/* Action Buttons at the bottom of content section */}
        {isEditing && (
          <div className="mt-8 flex items-center justify-end space-x-4 border-t border-gray-200 pt-8">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-8 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-2 px-10 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all active:scale-95 font-bold"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save className="w-5 h-5" />
              )}
              <span>Save Changes</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfile;
